/**
 * Theme preference: follow the system by default, with an explicit override.
 *
 * The palettes live in theme.css. This only decides which one applies, by
 * setting data-theme on <html> — absent means "follow the system", which is
 * what the CSS media query handles on its own.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';
export type TextSize = 'smaller' | 'normal' | 'large' | 'larger';

const KEY = 'givisnap:theme';
/*
 * The sizes moved down a step in October 2026: Normal became what Small had
 * been, a smaller one was added and the largest went. A choice saved before
 * that is under the old key, in the old names, and is read through
 * FROM_BEFORE so whoever picked a size keeps the same size.
 */
const SIZE_KEY = 'givisnap:textSize2';
const OLD_SIZE_KEY = 'givisnap:textSize';

/**
 * Normal is 0.85: the family found 1 large, so the step that fits more of the
 * thread on screen became where everybody starts. Smaller goes one step
 * further, 0.85 again; Larger stops at 1.15, since 1.3 left the composer and
 * the tab bar with too little of the screen.
 *
 * Everything below 1 relies on the input rule in theme.css, which keeps
 * fields at sixteen rendered pixels however far the page is scaled down --
 * under that, a phone zooms the whole page in when a field is tapped.
 */
const SCALE: Record<TextSize, number> = {
  smaller: 0.72,
  normal: 0.85,
  large: 1,
  larger: 1.15,
};

/** A size chosen under the old names, as the new name for the same size. */
const FROM_BEFORE: Record<string, TextSize> = {
  small: 'normal',
  normal: 'large',
  large: 'larger',
  // 1.3 is gone; the nearest is what Large was.
  larger: 'larger',
};

const SIZES = new Set<string>(['smaller', 'normal', 'large', 'larger']);

/** The size to use, from what this phone saved under the new key and the old. */
export function textSizeFrom(saved: string | null, savedBefore: string | null): TextSize {
  if (saved && SIZES.has(saved)) return saved as TextSize;
  if (savedBefore && savedBefore in FROM_BEFORE) return FROM_BEFORE[savedBefore];
  return 'normal';
}

/** Browser chrome colours, matching --ground in each palette. */
const CHROME = { dark: '#050608', light: '#f6f7fb' } as const;

export function readTheme(): ThemeChoice {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Private mode or blocked storage: fall through to following the system.
  }
  return 'system';
}

/** What the user will actually see, resolving 'system' against the OS. */
export function effectiveTheme(choice: ThemeChoice): 'light' | 'dark' {
  if (choice !== 'system') return choice;
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'system') delete root.dataset.theme;
  else root.dataset.theme = choice;

  // Keep the browser chrome in step. Both metas are set to the same value on
  // an explicit choice, which overrides their media attributes.
  const colour = CHROME[effectiveTheme(choice)];
  for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
    meta.setAttribute('content', colour);
  }
}

export function setTheme(choice: ThemeChoice): void {
  try {
    localStorage.setItem(KEY, choice);
  } catch {
    // The choice still applies for this session; it just will not persist.
  }
  applyTheme(choice);
}

export function readTextSize(): TextSize {
  try {
    return textSizeFrom(localStorage.getItem(SIZE_KEY), localStorage.getItem(OLD_SIZE_KEY));
  } catch {
    // Blocked storage: everyone gets the default size.
    return 'normal';
  }
}

export function applyTextSize(size: TextSize): void {
  document.documentElement.style.setProperty('--zoom', String(SCALE[size]));
}

/**
 * How far the text size scales the page, as the browser is applying it.
 *
 * A finger moves in screen pixels and everything under the zoom is laid out
 * in the page's own, so a drag that moves something by what the finger moved
 * falls behind it -- at Normal, by fifteen per cent. Divide by this first.
 */
export function pageZoom(): number {
  return parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
}

export function setTextSize(size: TextSize): void {
  try {
    localStorage.setItem(SIZE_KEY, size);
  } catch {
    // Applies for this session even if it will not persist.
  }
  applyTextSize(size);
}

/** Applied before first paint from main.tsx, so there is no flash of dark. */
export function initTheme(): ThemeChoice {
  const choice = readTheme();
  applyTheme(choice);
  applyTextSize(readTextSize());

  // Following the system means reacting when it changes mid-session.
  window
    .matchMedia('(prefers-color-scheme: light)')
    .addEventListener('change', () => {
      if (readTheme() === 'system') applyTheme('system');
    });

  return choice;
}
